import { AUDIENCE_TYPES } from "@/modules/Campaign/campaign.constant";

/*
  Pure pieces of the "paused audience on an existing line item" handling, kept
  out of the components so they can be tested without a DOM.

  The audience picker never lists a PAUSED pixel audience (D4), except the ids
  sent as `includeIds` — the line item's SAVED audiences, so an attached
  paused one still renders (named, segments resolved) and survives an
  unrelated re-save. It stays selectable until save, untick → re-tick
  included: the backend compares the payload with the saved rows, not with
  the form (R5, option A).

  A pixel audience whose first run has not completed (D10) is handled the same
  way, except it gets no tag (D10a) — only the warning under the field.
*/

/**
 * `includeIds` for the Visitor Insight picker: every saved BeeswaxAudiences
 * row of the line item (the pixel shims live there). Non-pixel ids are
 * harmless, since `includeIds` only lifts the paused and not-ready exclusions. Sorted and
 * deduplicated, because it is part of the query key.
 */
export function includeIdsFromSavedAudiences(
  audiences:
    | { audienceId: number | string; audienceModel: string }[]
    | undefined
): number[] {
  const ids = new Set<number>();
  for (const row of audiences ?? []) {
    if (row?.audienceModel !== AUDIENCE_TYPES.Audience) continue;
    const id = Number(row.audienceId);
    if (Number.isInteger(id) && id > 0) ids.add(id);
  }
  return Array.from(ids).sort((a, b) => a - b);
}

export interface PickerOption {
  label: string;
  value: number | string;
  /** A paused pixel audience, listed only because it is already attached. */
  paused?: boolean;
  /**
   * A pixel audience whose first run has not completed (`firstRunPending`),
   * listed only because it is already attached.
   */
  notReady?: boolean;
  /** The bare audience name, for copy; `label` carries the source suffix. */
  name?: string;
}

function selectedLabels(
  options: PickerOption[],
  selectedValues: (number | string)[],
  flag: "paused" | "notReady"
): string[] {
  const byValue = new Map(options.map((option) => [option.value, option]));
  return selectedValues
    .map((value) => byValue.get(value))
    .filter((option): option is PickerOption => Boolean(option?.[flag]))
    .map((option) => option.name ?? option.label);
}

/** Names of the selected options that are paused, in selection order. */
export const selectedPausedLabels = (
  options: PickerOption[],
  selectedValues: (number | string)[]
) => selectedLabels(options, selectedValues, "paused");

/** Names of the selected options whose first run has not completed. */
export const selectedNotReadyLabels = (
  options: PickerOption[],
  selectedValues: (number | string)[]
) => selectedLabels(options, selectedValues, "notReady");

const joinNames = (labels: string[]) =>
  `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

/** The amber helper under the field while a paused audience is selected. */
export function pausedSelectionHelper(labels: string[]): string | null {
  if (!labels.length) return null;
  if (labels.length === 1)
    return `${labels[0]} is paused. It stays on this line item, but if you remove it and save, it can't be added back until it's resumed.`;
  return `${joinNames(labels)} are paused. They stay on this line item, but one you remove and save can't be added back until it's resumed.`;
}

/** The amber helper while a selected audience's first run has not completed. */
export function notReadySelectionHelper(labels: string[]): string | null {
  if (!labels.length) return null;
  if (labels.length === 1)
    return `${labels[0]} hasn't finished its first run. It stays on this line item, but if you remove it and save, it can't be added back until its first run completes.`;
  return `${joinNames(labels)} haven't finished their first run. They stay on this line item, but one you remove and save can't be added back until its first run completes.`;
}
